import {
  Center,
  VStack,
  StackProps,
  Image,
  ImageProps,
  Divider,
  Box,
  HStack,
  Popover,
  PopoverAnchor,
  useMediaQuery,
} from '@chakra-ui/react';
import { Button, Link, Tooltip } from 'Atoms';
import { Typography } from 'Tokens';
import { useLocation } from 'react-router-dom';
import { SideMenuProps, SideMenuSection, SideMenuItem } from '.';
import { TFunction, useTranslation } from 'utils/translation';
import { useFeatureFlags, useHiddenFlags, useUserSetting } from '../Navigation.hooks';
import { useCurrentCompanyId, useCurrentCompany } from 'utils/hooks';
import { CollapseButton, SideMenuItems } from './SideMenuItems';
import { ProfileSidebar } from 'Organisms/ProfileSidebar/ProfileSidebar';
import { useContext, useEffect, useRef, useState } from 'react';
import { Walktour } from 'walktour';
import { GlobalContext } from 'containers/Navigation';
import { SIDEBAR_STRUCTURE_WALKTOUR } from 'utils/Walktours';
import React from 'react';
import { appContext } from 'App';

export const getModuleName = (t: TFunction, name: string) =>
  name ? t(`common:module.${name}`) : '';

const IconLogo = (props: ImageProps) => {
  const { usedColorMode } = useContext(appContext);
  return (
    <Image
      src={
        usedColorMode === 'new' ? '/assets/iss_logo_icon_custom.svg' : '/assets/iss_logo_icon.svg'
      }
      alt="ISS logo icon"
      {...props}
    />
  );
};

const TextLogo = (props: ImageProps) => {
  const { usedColorMode } = useContext(appContext);
  return (
    <Image
      src={
        usedColorMode === 'new' ? '/assets/iss_logo_text_custom.svg' : '/assets/iss_logo_text.svg'
      }
      alt="ISS Text Logo"
      {...props}
    />
  );
};

const CLOSED_SIDE_MENU_WIDTH = '56px';
const OPENED_SIDE_MENU_WIDTH = '228px';

const SideMenuWrapper = ({
  isOpen,
  isScreenLarge,
  ...props
}: { isOpen: boolean; isScreenLarge: boolean } & StackProps) => {
  if (isScreenLarge) {
    return (
      <VStack
        maxWidth={isOpen ? OPENED_SIDE_MENU_WIDTH : CLOSED_SIDE_MENU_WIDTH}
        minWidth={isOpen ? OPENED_SIDE_MENU_WIDTH : CLOSED_SIDE_MENU_WIDTH}
        bg="bg.sidebar"
        color="border.onAccent"
        p="4px 6px 8px"
        spacing="0px"
        transition="all 0.4s"
        justifyContent="space-between"
        overflow="hidden"
        borderRight="1px solid"
        borderColor="border.decorative"
        {...props}
      />
    );
  }
  return (
    <VStack
      minW={CLOSED_SIDE_MENU_WIDTH}
      bg="bg.accent"
      color="border.onAccent"
      p="4px 6px 8px"
      spacing="0px"
      justifyContent="space-between"
      pos={'relative'}
      overflow={!isOpen ? 'hidden' : 'visible'}
      {...props}
    />
  );
};

const HomeLink = ({ isOpen }: { isOpen: boolean }) => {
  const { companyId } = useCurrentCompanyId();
  return (
    <Link
      to={`/${companyId}`}
      color="white"
      textAlign="center"
      height="88px"
      width="100%"
      borderRadius="8px"
      py="16px"
      position="relative"
      _hover={{ bg: 'bg.hover' }}
      _pressed={{ bg: 'bg.pressed' }}
    >
      <Center as="span" width="100%" height="100%">
        <VStack spacing="8px" width="100%" alignItems="center">
          <HStack
            spacing="0px"
            width="100%"
            overflow="hidden"
            justifyContent="center"
            verticalAlign="center"
          >
            <TextLogo
              height="18px"
              width={isOpen ? 'auto' : 0}
              opacity={isOpen ? 1 : 0}
              transition="all 0.4s"
            />
            <IconLogo boxSize="18px" />
          </HStack>
          <Typography
            variant="micro"
            textAlign="center"
            textColor="text.sidebar"
            opacity={isOpen ? 1 : 0}
            width="max-content"
            height={isOpen ? 'auto' : 0}
            transition="all 0.4s"
          >
            Sustainability Reporting (prev. Celsia)
          </Typography>
        </VStack>
      </Center>
    </Link>
  );
};

export const SideMenuBlock = ({
  item,
  isOpen,
  isScreenLarge,
}: {
  item: SideMenuItem;
  isOpen: boolean;
  isScreenLarge: boolean;
}) => {
  const { company } = useCurrentCompany();
  const flags = useFeatureFlags({ company });
  const hiddenFlags = useHiddenFlags();
  const { pathname } = useLocation();
  const { t } = useTranslation(['common']);
  const isHome = item?.path === '/';
  const isCurrent = () => (isHome ? pathname === `/${company?.id}` : pathname.includes(item.path));

  if (hiddenFlags?.[item.key]) {
    return;
  }
  if (item.CustomButton) {
    return <item.CustomButton item={item} isOpen={isOpen} isScreenLarge={isScreenLarge} />;
  }

  if (flags?.[item.key as keyof typeof flags] === false) {
    return (
      <Tooltip placement="right" label={item?.disabledTooltip} mt="1px">
        <Box
          key={item.path}
          color="text.onAccent.disabled"
          width="100%"
          textDecoration="none"
          bg="bg.accent"
          cursor="not-allowed"
        >
          <HStack as="span" spacing="8px" p="12px">
            <item.Icon boxSize="20px" color="inherit" />
            <Typography
              color="inherit"
              variant="bodyStrong"
              minWidth="164px"
              transition="all 0.4s"
              noOfLines={1}
              opacity={isOpen ? 1 : 0}
            >
              {getModuleName(t, item.key)}
            </Typography>
          </HStack>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Popover placement="right" isOpen={false} closeOnBlur={false} isLazy={true}>
      <Link
        key={item.path}
        to={item.path === '/' ? `/${company?.id}` : item.path}
        color="text.sidebar"
        bg={isCurrent() ? 'bg.onAccent.selected' : 'none'}
        width="100%"
        borderRadius="8px"
        textDecoration="none"
        cursor="pointer"
        _pressed={{ bg: 'bg.pressed' }}
        _hover={{
          bg: 'bg.onAccent.hover.sidebar',
        }}
      >
        <PopoverAnchor>
          <Box>
            <Tooltip
              placement="right"
              label={!isOpen && isScreenLarge && getModuleName(t, item.key)}
              mt="1px"
              maxW="200px"
            >
              <HStack as="span" spacing="8px" padding="0 12px" height="36px">
                <item.Icon boxSize="20px" color="inherit" />
                <Typography
                  color="inherit"
                  variant="bodyStrong"
                  minWidth="164px"
                  transition="all 0.4s"
                  noOfLines={1}
                  opacity={isOpen ? 1 : 0}
                >
                  {getModuleName(t, item.key)}
                </Typography>
              </HStack>
            </Tooltip>
          </Box>
        </PopoverAnchor>
      </Link>
    </Popover>
  );
};

const SidebarWrapper = ({
  children,
  isScreenLarge,
  sideMenuState,
  isProfileSidebarMenuOpen,
  setSideMenuState,
}: {
  children: React.ReactNode;
  isScreenLarge: boolean;
  sideMenuState: boolean;
  isProfileSidebarMenuOpen: boolean;
  setSideMenuState: (value: boolean) => void;
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sideMenuState || isScreenLarge) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setSideMenuState(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sideMenuState, isScreenLarge, setSideMenuState]);

  if (isScreenLarge) {
    return (
      <VStack height={'100%'} width={'100%'}>
        {children}
      </VStack>
    );
  } else {
    return (
      <VStack
        ref={sidebarRef}
        height={'100%'}
        position={'absolute'}
        width={sideMenuState ? OPENED_SIDE_MENU_WIDTH : CLOSED_SIDE_MENU_WIDTH}
        onMouseEnter={() => {
          setSideMenuState(true);
        }}
        onMouseLeave={() => {
          if (!isProfileSidebarMenuOpen) {
            setSideMenuState(false);
          }
        }}
        left={0}
        right={0}
        top={0}
        bottom={0}
        padding={'4px 6px 8px'}
        bg={'bg.accent'}
        transition={'all 0.4s'}
        zIndex={10}
      >
        {children}
      </VStack>
    );
  }
};

export const SideMenu = ({ items }: SideMenuProps) => {
  const [isScreenLarge] = useMediaQuery('(min-width: 1400px)', {
    fallback: true,
  });
  const [isProfileSidebarMenuOpen, setIsProfileSidebarMenuOpen] = useState(false);
  const [sideMenuState, setSideMenuState] = useUserSetting('side-menu-state', true);
  const [walktourStatus, setWalktourStatus] = useUserSetting('sidebar-structure-walktour', '');
  const [isTourOpen, setIsTourOpen] = useState(false);
  const { walktourActive, setWalktourActive, setShowWalktourTooltip } = useContext(GlobalContext);

  const toggle = () => setSideMenuState(!sideMenuState);

  useEffect(() => {
    setIsTourOpen(walktourActive);
    if (walktourActive) {
      setWalktourStatus('');
    }
  }, [walktourActive, setWalktourStatus]);

  useEffect(() => {
    if (!walktourStatus || walktourStatus !== 'finished') {
      setIsTourOpen(true);
    } else {
      setIsTourOpen(false);
      if (walktourStatus === 'finished' && walktourActive) {
        setWalktourActive(false);
        setShowWalktourTooltip(true);
      }
    }
  }, [walktourStatus, walktourActive, setWalktourActive, setShowWalktourTooltip]);

  const customSidebarWalktourRenderer = (props: any) => {
    const handleClose = () => {
      setWalktourStatus('finished');
      props?.close();
    };

    return (
      <Box position="relative" display="flex" alignItems="center" gap="0">
        <Box
          backgroundColor="bg.accent"
          borderRadius="50%"
          w="6px"
          h="6px"
          zIndex="1"
          position="relative"
        />
        <Box borderColor="bg.accent" borderTop="2px solid" width="23px" height="1px" />
        <VStack
          backgroundColor="bg.accent"
          w="276px"
          borderRadius="8px"
          alignItems="start"
          gap="0"
          overflow="hidden"
          border="2px solid"
          borderColor="bg.accent"
        >
          <Box borderRadius="8px 8px 0px 0px" overflow="hidden" width="100%">
            <Image
              src="/images/new-structure-popover.png"
              alt="New sidebar structure"
              width="100%"
              height="auto"
              objectFit="cover"
            />
          </Box>
          <VStack spacing="8px" align="stretch" padding="16px 16px 0px 16px">
            <Typography variant="h3" color="text.onAccent">
              New structure
            </Typography>
            <Typography variant="body" color="text.onAccent">
              Notifications, Help center and your account are now moved to the side panel. Settings
              are available under your account.
            </Typography>
          </VStack>

          <Box w="100%" display="flex" justifyContent="flex-end" padding="16px">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Got it
            </Button>
          </Box>
        </VStack>
      </Box>
    );
  };
  useEffect(() => {
    if (!isScreenLarge) {
      setSideMenuState(false);
    }
  }, [isScreenLarge]);

  return (
    <>
      <Walktour
        rootSelector="#root"
        isOpen={isTourOpen}
        disableCloseOnClick
        tooltipSeparation={0}
        disableMaskInteraction={true}
        disableMask={true}
        customCloseFunc={(logic) => {
          setWalktourStatus('finished');
          logic.close(true);
        }}
        customTooltipRenderer={customSidebarWalktourRenderer}
        initialStepIndex={Number(walktourStatus) || 0}
        steps={SIDEBAR_STRUCTURE_WALKTOUR}
        nextLabel="Got it"
        prevLabel=""
      />

      <SideMenuWrapper
        isOpen={sideMenuState}
        isScreenLarge={isScreenLarge}
        className="walktour-help-center"
      >
        <SidebarWrapper
          isScreenLarge={isScreenLarge}
          sideMenuState={sideMenuState}
          setSideMenuState={setSideMenuState}
          isProfileSidebarMenuOpen={isProfileSidebarMenuOpen}
        >
          <VStack wrap="wrap" width="100%" spacing="2px" h="100%" alignItems="start">
            <HomeLink isOpen={sideMenuState} />
            {(() => {
              const sections = [
                SideMenuSection.primary,
                SideMenuSection.secondary,
                SideMenuSection.tertiary,
              ];
              const sectionsWithItems = sections.filter((section) =>
                items.some((item) => item.section === section)
              );
              return sectionsWithItems.map((section, index) => (
                <React.Fragment key={section}>
                  <SideMenuItems
                    section={section}
                    isOpen={sideMenuState}
                    items={items}
                    isScreenLarge={isScreenLarge}
                  />
                  {index < sectionsWithItems.length - 1 && (
                    <Center px="12px" py="8px" width="100%" alignItems="flex-start">
                      <Divider
                        border="1px"
                        borderRadius="3px"
                        borderColor="border.divider"
                        orientation="horizontal"
                      />
                    </Center>
                  )}
                </React.Fragment>
              ));
            })()}
          </VStack>
          <VStack wrap="wrap" width="100%" spacing="0px" alignItems="start">
            <VStack spacing="2px" width={'100%'} py="4px">
              <SideMenuItems
                section={SideMenuSection.general}
                isOpen={sideMenuState}
                items={items}
                isScreenLarge={isScreenLarge}
              />
            </VStack>
            <Center px="12px" py="8px" width="100%" alignItems="flex-start">
              <Divider
                border="1px"
                borderRadius="3px"
                borderColor="border.divider"
                orientation="horizontal"
              />
            </Center>
            <ProfileSidebar
              isOpen={sideMenuState}
              isScreenLarge={isScreenLarge}
              isMenuOpen={isProfileSidebarMenuOpen}
              setIsMenuOpen={setIsProfileSidebarMenuOpen}
            />
            <Center px="12px" py="8px" width="100%" alignItems="flex-start">
              <Divider
                border="1px"
                borderRadius="3px"
                borderColor="border.divider"
                orientation="horizontal"
              />
            </Center>
            <CollapseButton isOpen={sideMenuState} toggle={toggle} isScreenLarge={isScreenLarge} />
          </VStack>
        </SidebarWrapper>
      </SideMenuWrapper>
    </>
  );
};
